import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { type AdminDataProvider } from '../../../../api/adminDataProvider';
import { useDataProvider, useGetOne, useTranslate } from 'ra-core';
import { useQuery } from '@tanstack/react-query';
import { MoonstarTitle } from '../../../Moonstar/MoonstarTitle';
import { ChatSidebar } from './ChatSidebar';
import { ChatView } from './ChatView';
import { useEffect } from 'react';
import { Error } from '../../../../components/admin/error';
import { Loading } from '../../../../components/admin/loading';

export function UnitInsightsLlmChatPage() {
  const { unitId, insightId } = useParams<{
    unitId: string;
    insightId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const numericUnitId = Number(unitId);
  const numericInsightId = Number(insightId);
  const dataProvider = useDataProvider<AdminDataProvider>();
  const translate = useTranslate();

  const chatId = searchParams.get('chatId');
  const numericChatId = chatId ? Number(chatId) : undefined;

  const unit = useGetOne<{ id: number; name: string }>('units', {
    id: numericUnitId,
  });

  const latestInsight = useQuery({
    queryKey: ['unitLlmInsights', numericUnitId],
    queryFn: async () =>
      dataProvider.fetchUnitPerformanceLlmInsights(numericUnitId),
  });

  const chats = useQuery({
    queryKey: [
      'unitLlmChats',
      { unitId: numericUnitId, insightId: numericInsightId },
    ],
    queryFn: async () =>
      dataProvider.getUnitInsightLlmChats({
        unitId: numericUnitId,
        insightId: numericInsightId,
      }),
  });

  const chat = useQuery({
    queryKey: [
      'unitLlmChats',
      { unitId: numericUnitId, insightId: numericInsightId, chatId },
    ],
    queryFn: async () =>
      dataProvider.getUnitInsightLlmChat({
        unitId: numericUnitId,
        insightId: numericInsightId,
        chatId: Number(chatId),
      }),
    enabled: !!chatId,
  });

  useEffect(() => {
    if (latestInsight.isSuccess && latestInsight.data.id !== numericInsightId) {
      // Make sure all chats are for the **latest** insight
      void navigate(
        `/units/${unitId}/performance/insights/${latestInsight.data.id}/chat`
      );
    }
  }, [
    latestInsight.isSuccess,
    latestInsight.data?.id,
    numericInsightId,
    navigate,
    unitId,
  ]);

  function handleNewChatCreated(chatId: number) {
    void chats.refetch();
    setSearchParams({ chatId: String(chatId) });
  }

  return (
    <>
      <MoonstarTitle
        breadcrumb={[
          translate('resources.units.name', { count: 2 }),
          unit.data?.name || translate('resources.units.name', { count: 1 }),
          translate('resources.units.fields.performance'),
          translate('moonstar.unitPerformance.insights.title'),
        ]}
      />

      {unit.isPending ||
      chats.isPending ||
      latestInsight.isPending ||
      (chatId && chat.isPending) ? (
        <Loading />
      ) : unit.isError ||
        chats.isError ||
        latestInsight.isError ||
        (chatId && chat.isError) ? (
        <Error
          error={
            unit.error ||
            chats.error ||
            latestInsight.error ||
            (chatId && chat.error)
          }
          resetErrorBoundary={() => {
            if (unit.isError) {
              void unit.refetch();
            }
            if (chats.isError) {
              void chats.refetch();
            }
            if (latestInsight.isError) {
              void latestInsight.refetch();
            }
            if (chatId && chat.isError) {
              void chat.refetch();
            }
          }}
        />
      ) : (
        <div className="flex min-h-0 flex-col items-stretch gap-4 md:flex-row">
          <div className="w-full shrink-0 md:w-[170px]">
            <ChatSidebar chats={chats.data} />
          </div>
          <div className="min-w-0 flex-1">
            <ChatView
              key={chatId}
              unitId={unit.data.id}
              chatId={numericChatId}
              chat={chat.data}
              insight={latestInsight.data}
              refetchChatHistory={async () => {
                await chat.refetch();
              }}
              newChatCreated={(chatId) => handleNewChatCreated(chatId)}
            />
          </div>
        </div>
      )}
    </>
  );
}
